/**
 * @fileoverview Generic chart/table card shell.
 * @description The one card frame wrapping any chart or table on a dashboard-area page — a
 * title/subtitle/optional-action header row over a body slot, with a loading-skeleton state that
 * keeps the header visible so the page doesn't feel like it's fully reloading. Ported from the
 * InsightBox Banking design system's own `ChartCard`, generalized (no banking-specific coupling)
 * since any module's chart-bearing pages need the same shell.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Skeleton from '@/shared/components/atoms/Skeleton'

import type { CSSProperties, ReactNode } from 'react'

interface ChartCardProps {
  title: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
  dense?: boolean
  fill?: boolean
  centerBody?: boolean
  loading?: boolean
}

/**
 * @component
 * @param {ChartCardProps} props - The card's header content, body, and layout/loading switches.
 * @param {string} props.title - The card's heading.
 * @param {string} [props.subtitle] - An optional line after the title, separated by a rule.
 * @param {ReactNode} [props.action] - Right-aligned header content (e.g. a filter toggle).
 * @param {ReactNode} props.children - The card's body (a chart, table, or breakdown).
 * @param {boolean} [props.dense] - Tighter padding/margins for pages that must fit one viewport
 * with no scroll. Off by default.
 * @param {boolean} [props.fill] - Grows to fill the remaining vertical space in a flex parent, for
 * a page's "hero" chart. The body gets its own `overflow-y-auto` safety net (with
 * `scrollbar-gutter: stable`, so the scrollbar toggling on/off doesn't trigger a resize-redraw
 * loop in a responsive chart) and a `min-h-[280px]` floor. Off by default.
 * @param {boolean} [props.centerBody] - Stretches the body to the card's full height and centers
 * its content vertically — for a card whose height is set by a taller sibling (e.g. two cards in
 * an equal-height grid row). Skips `fill`'s overflow-y-auto safety net, since this is for content
 * already known to fit. Off by default.
 * @param {boolean} [props.loading] - Shows a shimmer in place of the body, keeping the header
 * visible. Off by default.
 */
function ChartCard({
  title,
  subtitle,
  action,
  children,
  dense = false,
  fill = false,
  centerBody = false,
  loading = false,
}: ChartCardProps) {
  const bodyStyle: CSSProperties | undefined = fill ? { scrollbarGutter: 'stable' } : undefined

  return (
    <div
      className={`flex flex-col rounded-2xl border border-border bg-white shadow-card ${dense ? 'p-4' : 'p-5 md:p-6'} ${fill ? 'min-h-0 flex-1' : ''}`}
    >
      <div
        className={`flex flex-wrap items-start justify-between gap-3 ${dense ? 'mb-2' : 'mb-5'}`}
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h2 className={`font-bold text-text ${dense ? 'text-sm' : 'text-base md:text-lg'}`}>
            {title}
          </h2>
          {subtitle && (
            <p className="flex items-baseline gap-2 text-xs text-label">
              <span aria-hidden="true" className="text-border">
                |
              </span>
              {subtitle}
            </p>
          )}
        </div>
        {action}
      </div>
      {loading ? (
        <Skeleton width="100%" height={fill ? '100%' : 256} />
      ) : (
        <div
          className={`${fill ? 'flex min-h-[280px] flex-1 flex-col overflow-y-auto' : ''} ${centerBody ? 'flex flex-1 flex-col justify-center' : ''}`}
          style={bodyStyle}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export default ChartCard
